import { CUSTOMER_FEATURE_CATALOG, type KnownCustomerFeatureKey } from './customerFeaturesCatalog';
import { FEATURE_FLAG_CATALOG, type KnownFeatureFlagKey } from './featureFlagsCatalog';

export type KnownFeatureAccessKey = KnownFeatureFlagKey | KnownCustomerFeatureKey;
export type FeatureAccessSource = 'beta_feature' | 'customer_feature';

export type FeatureAccessCatalogEntry = {
  description: string;
  key: KnownFeatureAccessKey;
  name: string;
  source: FeatureAccessSource;
  surfaces: string[];
};

export const FEATURE_ACCESS_CATALOG: readonly FeatureAccessCatalogEntry[] = [
  ...FEATURE_FLAG_CATALOG.map((entry) => ({
    key: entry.key,
    name: entry.name,
    description: entry.description,
    source: 'beta_feature' as const,
    surfaces: [...entry.surfaces],
  })),
  ...CUSTOMER_FEATURE_CATALOG.map((entry) => ({
    key: entry.key,
    name: entry.name,
    description: entry.description,
    source: 'customer_feature' as const,
    surfaces: [...entry.surfaces],
  })),
] as const;

// A key MAY be registered under more than one source — an exposure flag
// (beta_feature) and an entitlement (customer_feature) are different
// access-control axes (AGENTS.md access-control model: flags are exposure,
// entitlements are access). The guard stays defensive: callers that need an
// entitlement MUST pass
// `source: 'customer_feature'`, and without a source an ambiguous key fails
// closed (null) rather than silently resolving to the first (exposure) entry —
// which would let an enabled exposure flag masquerade as an entitlement grant.
export function getFeatureAccessCatalogEntry(
  key: string,
  source?: FeatureAccessSource,
) {
  const matches = FEATURE_ACCESS_CATALOG.filter((entry) => entry.key === key);
  if (source) {
    return matches.find((entry) => entry.source === source) ?? null;
  }
  if (matches.length > 1) return null;
  return matches[0] ?? null;
}
